import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LayoutSubmasterToUser from "../../components/layoutConfig/layoutSubmasterToUser";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "animate.css";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import DataTable, { TableColumn } from "react-data-table-component";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";

/* ---------------------- Types ---------------------- */
interface History {
  id: number;
  data_vencimento: string;
  created_at: string;
  caminho_doc?: string;
}

interface UserAccess {
  id: number;
  user_id: number;
  name: string;
  email: string;
  can_view?: boolean;
}

interface Location {
  id: number;
  name: string;
}

interface Signatory {
  id?: number;
  email: string;
  user_id?: number | null;
}

interface ContractData {
  id: number;
  name?: string;
  caminho_doc?: string;
  data_vencimento?: string;
  created_at?: string;
  updated_at?: string;
  history?: History[];
  locations?: Location[];
  users_access?: UserAccess[];
  signatories?: Signatory[];
}

/* ---------------------- Component ---------------------- */
export default function HomeUserToSubmasterPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const name = queryParams.get("name");

  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthTokenUserToSubmaster());
  const ApiClient = setupAPIclient(token);

  const [contracts, setContracts] = useState<ContractData[]>([]);
  const [filteredContracts, setFilteredContracts] = useState<ContractData[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ContractData | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [signatories, setSignatories] = useState<Signatory[]>([{ email: "" }]);

  const [filterId, setFilterId] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterDate, setFilterDate] = useState("");

  /* ---------------------- Fetch Contracts ---------------------- */
  const handleGetContract = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.post(`/management/get/contract`);
      const payload = response.data?.success ?? [];

      const mappedContracts: ContractData[] = payload.map((c: any) => ({
        id: c.id,
        name: c.name,
        caminho_doc: c.caminho_doc,
        data_vencimento: c.data_vencimento,
        history: c.management_history,
        signatories: c.management_signatories,
        users_access: c.management_user_uploaddoc_access?.map((u: any) => ({
          id: u.id,
          user_id: u.user_id,
          name: u.management_user.name,
          email: u.management_user.email,
          can_view: u.can_view
        })),
        locations: c.management_document_access?.map((l: any) => ({
          id: l.id,
          name: `Local ${l.id}`
        }))
      }));

      setContracts(mappedContracts);
      setFilteredContracts(mappedContracts);
    } catch (err) {
      console.error("Erro ao buscar contratos:", err);
      toast.error("Erro ao buscar contratos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Valor recebido via query string:", name);
    handleGetContract();
  }, [name]);

  /* ---------------------- Filters ---------------------- */
  useEffect(() => {
    let temp = [...contracts];
    if (filterId.trim()) temp = temp.filter(c => c.id.toString().includes(filterId.trim()));
    if (filterLocation.trim()) {
      temp = temp.filter(c => (c.locations || []).some(l => (l.name || "").toLowerCase().includes(filterLocation.toLowerCase())));
    }
    if (filterDate) {
      temp = temp.filter(c => {
        if (!c.data_vencimento) return false;
        const d1 = new Date(c.data_vencimento);
        const d2 = new Date(filterDate);
        d1.setHours(0,0,0,0);
        d2.setHours(0,0,0,0);
        return d1.getTime() === d2.getTime();
      });
    }
    setFilteredContracts(temp);
  }, [filterId, filterLocation, filterDate, contracts]);

  /* ---------------------- Modal ---------------------- */
  const openModal = (contract: ContractData) => {
    setSelectedContract(contract);
    setPreviewUrl(contract.caminho_doc ? `https://alexios.com.br:9598/${contract.caminho_doc}` : null);
    const initialSignatories: Signatory[] = contract.signatories && contract.signatories.length
      ? contract.signatories
      : contract.users_access?.map(u => ({ email: u.email, user_id: u.user_id })) ?? [{ email: "" }];
    setSignatories(initialSignatories);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContract(null);
    setPreviewUrl(null);
    setSignatories([{ email: "" }]);
  };

  const handleViewHistory = (caminho_doc?: string) => {
    if (caminho_doc) setPreviewUrl(`https://alexios.com.br:9598/${caminho_doc}`);
  };

  /* ---------------------- DataTable Columns ---------------------- */
  const columns: TableColumn<ContractData>[] = useMemo(() => {
    const renderBadge = (content: string | number, color: string) => (
      <span className={`badge bg-${color}`}>{content}</span>
    );

    const renderVencimento = (date?: string) => {
      if (!date) return renderBadge("-", "secondary");
      const d = new Date(date);
      if (isNaN(d.getTime())) return renderBadge(date, "warning");
      const today = new Date(); today.setHours(0,0,0,0);
      const past = d < today;
      return renderBadge(d.toLocaleDateString('pt-BR'), past ? "danger" : "success");
    };

    const renderUsersAccess = (users?: UserAccess[]) => {
      if (!users || users.length === 0) return "-";
      return (
        <div style={{ display: "flex", gap: 4 }}>
          {users.map(u => (
            <OverlayTrigger key={u.id} placement="top" overlay={<Tooltip>{u.email}</Tooltip>}>
              <div className="d-flex justify-content-center align-items-center text-white" style={{
                width: 28, height: 28, borderRadius: "50%", backgroundColor: "#0d6efd",
                fontSize: "0.85rem", fontWeight: 600
              }}>
                {u.name[0].toUpperCase()}
              </div>
            </OverlayTrigger>
          ))}
        </div>
      );
    };

    const renderActions = (row: ContractData) => (
      <div className="d-flex gap-2">
        <OverlayTrigger placement="top" overlay={<Tooltip>Ver detalhes</Tooltip>}>
          <button className="btn btn-sm btn-outline-primary rounded-pill shadow-sm" onClick={() => openModal(row)}>
            <i className="bi bi-eye"></i>
          </button>
        </OverlayTrigger>
        <OverlayTrigger placement="top" overlay={<Tooltip>Download</Tooltip>}>
          <a className="btn btn-sm btn-outline-secondary rounded-pill shadow-sm"
             href={row.caminho_doc ? `https://alexios.com.br:9598/${row.caminho_doc}` : "#"}
             target="_blank" rel="noreferrer">
            <i className="bi bi-download"></i>
          </a>
        </OverlayTrigger>
      </div>
    );

    return [
      { name: "ID", selector: row => row.id, sortable: true, width: "80px", center: true, cell: row => renderBadge(row.id, "dark") },
      { name: "Nome", selector: row => row.name || "-", sortable: true, cell: row => <span className="fw-bold">{row.name || "-"}</span> },
      { name: "Vencimento", selector: row => row.data_vencimento, sortable: true, width: "150px", cell: row => renderVencimento(row.data_vencimento) },
      { name: "Local", selector: row => row.locations?.map(l => l.name).join(", ") || "-", sortable: false },
      { name: "Acessos", selector: row => row.users_access?.length || 0, sortable: true, cell: row => renderUsersAccess(row.users_access) },
      { name: "Ações", cell: renderActions, width: "120px", center: true }
    ];
  }, []);

  /* ---------------------- Render ---------------------- */
  return (
    <LayoutSubmasterToUser>
      <div className="container mt-3">
        <h3>Contratos</h3>
        <div className="d-flex gap-2 mb-3">
          <input type="text" className="form-control" placeholder="Filtrar por ID" value={filterId} onChange={e => setFilterId(e.target.value)} />
          <input type="text" className="form-control" placeholder="Filtrar por Local" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} />
          <input type="date" className="form-control" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        </div>

        <DataTable
          columns={columns}
          data={filteredContracts}
          progressPending={loading}
          pagination
          highlightOnHover
          striped
          responsive
        />
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes do Contrato {selectedContract?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewUrl ? (
            <iframe src={previewUrl} width="100%" height="400px" title="Preview do Documento" />
          ) : (
            <p>Nenhum documento disponível</p>
          )}

          <h5 className="mt-3">Histórico de Contratos</h5>
          {selectedContract?.history?.length ? (
            <ul className="list-group mb-3">
              {selectedContract.history.map(h => (
                <li key={h.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{new Date(h.data_vencimento).toLocaleDateString('pt-BR')}</span>
                  <div className="d-flex gap-2">
                    {h.caminho_doc && (
                      <>
                        <a href={`https://alexios.com.br:9598/${h.caminho_doc}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-download"></i> Download
                        </a>
                        <button className="btn btn-sm btn-outline-success" onClick={() => handleViewHistory(h.caminho_doc)}>
                          <i className="bi bi-eye"></i> Ver
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>Não há histórico de documentos.</p>
          )}

          <h5 className="mt-3">Integrantes</h5>
          {signatories.map((s, idx) => (
            <div key={idx} className="mb-2">
              <input type="email" className="form-control" value={s.email} readOnly />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeModal}>Fechar</button>
        </Modal.Footer>
      </Modal>
    </LayoutSubmasterToUser>
  );
}
